import { useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation } from '@tanstack/react-query'
import { Navigate, Link, useNavigate } from 'react-router-dom'
import ReCAPTCHA from 'react-google-recaptcha'

import { useTheme } from '@/components/ThemeProvider'
import SplitLayout from '@/components/Onboarding/Layout/SplitLayout'
import { useCurrentUser } from '@contexts/currentUser'
import { client } from '@helpers/api'
import { Button } from '@components/ui/button'
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
  FormDescription
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'

const formSchema = z.object({
  email: z.string()
    .min(1, 'Email is required')
    .email('Email must be valid')
    .refine(
      (email) => !['gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'live.com', 'icloud.com'].some(domain => email.includes(domain)),
      'Please use your school email address'
    ),
  password: z.string()
    .min(1, 'Password is required')
    .min(8, 'Password must be at least 8 characters')
})

const EducatorSignUp = () => {
  const captchaRef = useRef(null)
  const navigate = useNavigate()
  const { resolvedTheme } = useTheme()

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: '',
      password: '',
      captcha: ''
    }
  })

  const { user: { signedIn } } = useCurrentUser()

  const { mutate: signUp, isLoading } = useMutation({
    mutationFn: async variables => await client.post('/sign_up', variables),
    onSuccess: response => {
      const email = form.getValues('email')
      navigate(`/email_confirmation?email=${email}&sent=true`)
    },
    onError: error => {
      const message = error?.response?.data?.error

      if (message === 'Email has already been taken') {
        form.setError('email', { message })
      } else if (message === 'Invitation is invalid') {
        form.setError('root', { message: 'Oops! Your invitation doesn\'t seem to be working. You may have used it already, or it expired.' })
      } else {
        form.setError('root', { message: 'Oops! Something went wrong.' })
      }
    }
  })

  const onSubmit = (data) => {
    const captcha = captchaRef.current.getValue()

    if (!captcha) {
      form.setError('captcha', { message: 'Please verify that you are not a robot.' })
      return
    }

    signUp({ ...data, captcha })
  }

  if (signedIn) {
    return <Navigate to='/educators' />
  }

  const cookies = document.cookie.split('; ')
  const csrfToken = cookies?.find(cookie => cookie.startsWith('CSRF-TOKEN='))?.split('=')[1]

  return (
    <SplitLayout>
      <h1 className='text-2xl font-bold'>Welcome to Mindjoy!</h1>
      <p className='text-sm mt-2 mb-6'>
        Mindjoy helps you bring AI-powered learning to your classroom.<br />Sign up to get started!
      </p>

      <form method='post' action={`${import.meta.env.VITE_API_URL}/users/auth/google_app?intent=sign_up`}>
        <input type='hidden' name='authenticity_token' value={csrfToken} />

        <Button
          type='submit'
          variant='outline'
          className='text-center w-full'
        >
          <img
            className='w-5'
            src='google-logo.svg'
          />

          <span>Sign up with Google</span>
        </Button>
      </form>

      <form method='post' action={`${import.meta.env.VITE_API_URL}/users/auth/microsoft_app?intent=sign_up`}>
        <input type='hidden' name='authenticity_token' value={csrfToken} />

        <Button
          type='submit'
          variant='outline'
          className='text-center w-full mt-3'
        >
          <img
            className='w-5'
            src='microsoft-logo.svg'
          />

          <span>Sign up with Microsoft</span>
        </Button>
      </form>

      <div className='flex items-center mt-4'>
        <div className='border-b w-full' />
        <span className='p-3 text-sm text-muted-foreground'>OR</span>
        <div className='border-b w-full' />
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className='flex flex-col space-y-4 mt-3'>

          <If condition={form.formState.errors.root}>
            <p className='text-sm mt-1 text-red-500' role='alert'>
              {form.formState.errors.root.message}
            </p>
          </If>

          <FormField
            control={form.control}
            name='email'
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input placeholder='name@school-email.com' {...field} />
                </FormControl>
                <FormDescription>Use your organization email if possible</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name='password'
            render={({ field }) => (
              <FormItem>
                <FormLabel>Password</FormLabel>
                <FormControl>
                  <Input type='password' placeholder='At least 8 characters' {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className='mt-2'>
            <ReCAPTCHA
              sitekey={import.meta.env.VITE_RECAPTCHA_SITE_KEY}
              ref={captchaRef}
            />
            <If condition={form.formState.errors.captcha}>
              <p className='text-sm mt-1 text-red-500' role='alert'>{form.formState.errors.captcha.message}</p>
            </If>
          </div>

          <p className='leading-tight mt-3 text-sm'>
            By continuing, you agree to Mindjoy's <a className='underline' href='https://www.mindjoy.com/terms'>Terms of Service</a> and <a className='underline' href='https://www.mindjoy.com/privacy'>Privacy Policy</a>.
          </p>

          <Button type='submit' loading={isLoading}>
            {isLoading ? 'Creating account...' : 'Create account'}
          </Button>

          <p className='text-sm text-center'>
            Already have an account? <Link className='underline' to='/sign_in'>Sign in</Link>
          </p>
        </form>
      </Form>

      <SplitLayout.SidePanel>
        <Choose>
          <When condition={resolvedTheme === 'dark'}>
            <div className='h-full w-full bg-right bg-no-repeat bg-contain bg-[url(/onboarding/classroom-insights-dark@2x.png)]' />
          </When>
          <Otherwise>
            <div className='h-full w-full bg-right bg-no-repeat bg-contain bg-[url(/onboarding/classroom-insights@2x.png)]' />
          </Otherwise>
        </Choose>
      </SplitLayout.SidePanel>
    </SplitLayout>
  )
}

export default EducatorSignUp
